<script setup lang="ts">
import { capitalize } from 'lodash'
import { useI18n } from 'vue-i18n'
import { VTabList } from '@/modules/shared/components'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { computed, onMounted } from 'vue'
import { rails_url } from '@/modules/shared/utils/rails'
import { useOutstandingStore } from '@/modules/dashboard/stores/outstanding-store'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'

defineProps<{
  selectedTab:
    | 'account'
    | 'esignatures'
    | 'files'
    | 'invoices'
    | 'notes'
    | 'overview'
    | 'portfolio'
    | 'funds'
    | 'spvs'
    | 'associated-entities'
    | 'taxes'
    | 'file_system'
}>()

const { individual_id } = useRoute().params as { individual_id: string }
const { t } = useI18n()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const outstandingStore = useOutstandingStore()
const individualStore = useIndividualStore()
const outstanding = computed(() => outstandingStore.outstanding)

const individualURL = computed(() => `${rails_url()}/investors/${individual_id}`)

const taxYear = new Date().getFullYear() - 1

onMounted(async () => {
  await outstandingStore.fetchOutstandings(`individual:${individual_id}`)
})
</script>

<template>
  <div class="mb-12 px-4 sm:px-0">
    <VTabList
      :tabs="[
        {
          current: selectedTab === 'overview',
          name: capitalize(t('shared.overview')),
          to: {
            name: 'investing.individual-overview',
          },
        },
        {
          current: selectedTab === 'portfolio',
          name: capitalize(t('shared.portfolio', 0)),
          to: {
            name: 'investing.individual.portfolio',
          },
        },
        {
          current: selectedTab === 'funds',
          name: 'Funds',
          to: {
            name: 'investing.individual.funds',
            params: { individual_id: individual_id },
          },
        },
        {
          current: selectedTab === 'spvs',
          name: 'SPVs',
          to: {
            name: 'investing.individual.spvs',
            params: { individual_id: individual_id },
          },
        },
        {
          current: selectedTab === 'associated-entities',
          name: 'Associated Entities',
          to: {
            name: 'investing.individual.associated-entities',
            params: { individual_id: individual_id },
          },
          count: outstanding.incomplete_associated_entities_count,
        },
        {
          current: selectedTab === 'invoices',
          name: capitalize(t('shared.invoice', 0)),
          to: {
            name: 'investing.individual-invoices',
          },
          count: outstanding.invoices_count,
        },
        {
          current: selectedTab === 'esignatures',
          name: t('shared.eSignature', 0),
          to: {
            name: 'investing.individual.documents',
          },
          count: outstanding.documents_count,
        },
        {
          current: selectedTab === 'file_system',
          name: 'Files',
          to: {
            name: 'filing.individual.files-system',
            params: {
              individual_id: individual_id,
            },
          },
          visible: !!individualStore.individual?.filing_folder_root_id,
        },
        {
          current: selectedTab === 'taxes',
          name: capitalize(t('shared.tax', 0)),
          visible: workspaceStore.current?.has_managed_entities,
          to: {
            name: 'investing.individual.taxes',
            query: { year: taxYear },
            params: {
              individual_id: individual_id,
            },
          },
        },
        {
          current: selectedTab === 'account',
          name: capitalize(t('shared.account')),
          to: {
            name: 'investing.individual.account',
            params: {
              individual_id: individual_id,
            },
          },
        },
        {
          current: selectedTab === 'notes',
          name: capitalize(t('shared.note', 0)),
          visible: authStore.is_site_or_group_admin,
          to: {
            name: 'investing.individual.notes',
            params: { individual_id: individual_id },
          },
        },
        {
          current: false,
          name: 'Files (legacy)',
          to: `${individualURL}/files?legacy=true`,
          visible: authStore.is_site_admin,
        },
      ]"
    />
  </div>
</template>
